'use client'

import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/utils'

const avatarVariants = cva('relative flex items-center justify-center shrink-0 overflow-hidden select-none', {
  variants: {
    variant: {
      basic: 'rounded-full',
      square: 'rounded-md'
    },
    size: {
      '2xs': 'h-4 w-4 text-[8px] rounded-sm',
      xs: 'h-6 w-6 text-xs',
      sm: 'h-8 w-8 text-sm',
      md: 'h-12 w-12 text-base',
      lg: 'h-16 w-16 text-lg',
      xl: 'h-24 w-24 text-xl',
      '2xl': 'h-32 w-32 text-2xl'
    }
  },
  defaultVariants: {
    variant: 'basic',
    size: 'md'
  }
})

interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string
  alt?: string
  name: string
  showBorder?: boolean
  loading?: 'eager' | 'lazy'
  onError?: () => void
}

const colorPairs = [
  { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-700 dark:text-blue-200' },
  { bg: 'bg-rose-100 dark:bg-rose-900', text: 'text-rose-700 dark:text-rose-200' },
  { bg: 'bg-violet-100 dark:bg-violet-900', text: 'text-violet-700 dark:text-violet-200' },
  { bg: 'bg-amber-100 dark:bg-amber-900', text: 'text-amber-700 dark:text-amber-200' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900', text: 'text-emerald-700 dark:text-emerald-200' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900', text: 'text-cyan-700 dark:text-cyan-200' },
  { bg: 'bg-fuchsia-100 dark:bg-fuchsia-900', text: 'text-fuchsia-700 dark:text-fuchsia-200' },
  { bg: 'bg-lime-100 dark:bg-lime-900', text: 'text-lime-700 dark:text-lime-200' },
  { bg: 'bg-indigo-100 dark:bg-indigo-900', text: 'text-indigo-700 dark:text-indigo-200' },
  { bg: 'bg-teal-100 dark:bg-teal-900', text: 'text-teal-700 dark:text-teal-200' }
]

const getColorFromName = (name: string) => {
  const index = Math.abs(name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colorPairs.length
  return colorPairs[index]
}

export const Avatar = ({
  className,
  variant,
  size,
  src,
  alt,
  name,
  showBorder,
  loading,
  onError,
  ...props
}: AvatarProps) => {
  const { bg, text } = getColorFromName(name)
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  // Convert empty strings, null, or undefined to undefined to avoid the empty src warning
  const imageSrc = src && src.trim().length > 0 ? src : undefined

  return (
    <AvatarPrimitive.Root
      className={cn(
        avatarVariants({ variant, size }),
        showBorder && 'ring-2 ring-offset-2 ring-offset-background',
        className
      )}
      {...props}
    >
      <AvatarPrimitive.Image
        src={imageSrc}
        alt={alt ?? name}
        loading={loading}
        onError={onError}
        className="aspect-square h-full w-full"
      />
      <AvatarPrimitive.Fallback className={cn('h-full w-full flex items-center justify-center', bg, text)}>
        {initials}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}

Avatar.displayName = 'Avatar'
